import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import professionalTheme from './theme';
import { CssBaseline, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Board from './pages/Board';
import Boards from './pages/Boards';
import Issues from './pages/Issues';
import Sprints from './pages/Sprints';
import Teams from './pages/Teams';
import Users from './pages/Users';
import Employees from './pages/Employees';
import Planning from './pages/Planning';
import Timesheets from './pages/Timesheets';
import TimesheetForm from './pages/TimesheetForm';
import TimesheetApprovals from './pages/TimesheetApprovals';
import AdminTasks from './pages/AdminTasks';
import Leaves from './pages/Leaves';
import LeaveForm from './pages/LeaveForm';
import LeaveApprovals from './pages/LeaveApprovals';
import LeaveCalendar from './pages/LeaveCalendar';
import Features from './pages/Features';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Unauthorized from './pages/Unauthorized';
import ProjectAssignments from './pages/ProjectAssignments';
import EstimationTool from './pages/EstimationTool';
import JobDetails from './pages/JobDetails';
import JobEstimation from './pages/JobEstimation';
import EstimationConfig from './pages/EstimationConfig';
import AdvancedEstimation from './pages/AdvancedEstimation';
import HistoricalAnalytics from './pages/HistoricalAnalytics';
import ProductivityAnalytics from './pages/ProductivityAnalytics';
import EstimationTemplates from './pages/EstimationTemplates';
import LeadershipDashboard from './pages/LeadershipDashboard';
import CustomKPIBuilder from './pages/CustomKPIBuilder';
import UserProductivity from './pages/UserProductivity';
import RootAdminDashboard from './pages/RootAdminDashboard';
import Retrospectives from './pages/Retrospectives';
import Velocity from './pages/Velocity';
import TimesheetCalendar from './pages/TimesheetCalendar';
import LandingPage from './pages/Landing';
import SignUp from './pages/SignUp';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/Common/ErrorBoundary';
import RoleBasedRoute from './components/RoleBasedRoute';


function App() {
  return (
    <ThemeProvider theme={professionalTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ErrorBoundary>
          <AuthProvider>
            <Router>
              <Routes>
                  {/* Public Landing Page (default) */}
                  <Route path="/landing" element={<LandingPage />} />
                  <Route path="/" element={<LandingPage />} />
                  
                  {/* Auth Routes */}
                  <Route path="/login" element={<Navigate to="/auth" replace />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/register" element={<SignUp />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  
                  {/* Protected App Routes */}
                  <Route path="/*" element={<PrivateRoute><Layout /></PrivateRoute>}>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="root-admin-dashboard" element={
                      <RoleBasedRoute allowedRoles={['root_user']}>
                        <RootAdminDashboard />
                      </RoleBasedRoute>
                    } />
                    <Route path="projects" element={<Projects />} />
                    <Route path="projects/:id" element={<ProjectDetail />} />
                    <Route path="board/:projectId" element={<Board />} />
                    <Route path="boards" element={<Boards />} />
                    <Route path="issues" element={<Issues />} />
                    <Route path="sprints" element={<Sprints />} />
                    <Route path="retrospectives" element={<Retrospectives />} />
                    <Route path="velocity" element={<Velocity />} />
                    <Route path="teams" element={<Teams />} />
                    <Route path="users" element={<Users />} />
                    <Route path="employees" element={
                      <PrivateRoute>
                        <RoleBasedRoute allowedRoles={['root_user', 'admin', 'hr_admin', 'super_manager']}>
                          <Employees />
                        </RoleBasedRoute>
                      </PrivateRoute>
                    } />
                    <Route path="planning" element={<Planning />} />
                    <Route path="timesheets" element={<Timesheets />} />
                    <Route path="timesheets/new" element={<TimesheetForm />} />
                    <Route path="timesheets/:id" element={<TimesheetForm />} />
                    <Route path="timesheets/approvals" element={
                      <PrivateRoute>
                        <RoleBasedRoute allowedRoles={['root_user', 'admin', 'hr_admin', 'super_manager', 'manager']}>
                          <TimesheetApprovals />
                        </RoleBasedRoute>
                      </PrivateRoute>
                    } />
                    <Route path="timesheets/calendar" element={
                      <PrivateRoute>
                        <RoleBasedRoute allowedRoles={['admin', 'hr_admin', 'super_manager', 'manager']}>
                          <TimesheetCalendar />
                        </RoleBasedRoute>
                      </PrivateRoute>
                    } />
                    <Route path="admin-tasks" element={
                      <PrivateRoute>
                        <RoleBasedRoute allowedRoles={['root_user', 'admin', 'hr_admin', 'super_manager']}>
                          <AdminTasks />
                        </RoleBasedRoute>
                      </PrivateRoute>
                    } />
                    <Route path="leaves" element={<Leaves />} />
                    <Route path="leaves/calendar" element={<LeaveCalendar />} />
                    <Route path="leaves/new" element={<LeaveForm />} />
                    <Route path="leaves/approvals" element={
                      <PrivateRoute>
                        <RoleBasedRoute allowedRoles={['root_user', 'admin', 'hr_admin', 'super_manager', 'manager']}>
                          <LeaveApprovals />
                        </RoleBasedRoute>
                      </PrivateRoute>
                    } />
                    <Route path="leaves/:id" element={<LeaveForm />} />
                            <Route path="features" element={<Features />} />
        <Route path="estimation" element={
          <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
            <EstimationTool />
          </RoleBasedRoute>
        } />
               <Route path="estimation/job/:jobId" element={
         <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
           <JobDetails />
         </RoleBasedRoute>
       } />
       <Route path="estimation/job/:jobId/estimation" element={
         <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
           <JobEstimation />
         </RoleBasedRoute>
       } />
        <Route path="estimation/config" element={
          <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
            <EstimationConfig />
          </RoleBasedRoute>
        } />
        <Route path="estimation/advanced" element={
          <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
            <AdvancedEstimation />
          </RoleBasedRoute>
        } />
        <Route path="estimation/analytics" element={
          <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
            <HistoricalAnalytics />
          </RoleBasedRoute>
        } />
        <Route path="estimation/templates" element={
          <RoleBasedRoute allowedRoles={['manager', 'admin', 'super_manager', 'hr_admin', 'root_user']}>
            <EstimationTemplates />
          </RoleBasedRoute>
        } />
        <Route path="productivity-analytics" element={
          <RoleBasedRoute allowedRoles={['root_user', 'admin', 'manager', 'super_manager', 'hr', 'hr_admin']}>
            <ProductivityAnalytics />
          </RoleBasedRoute>
        } />
        <Route path="leadership-dashboard" element={
          <RoleBasedRoute allowedRoles={['root_user', 'admin', 'super_manager', 'hr_admin']}>
            <LeadershipDashboard />
          </RoleBasedRoute>
        } />
        <Route path="custom-kpis" element={
          <RoleBasedRoute allowedRoles={['root_user', 'admin', 'super_manager', 'hr_admin']}>
            <CustomKPIBuilder />
          </RoleBasedRoute>
        } />
        <Route path="user-productivity" element={
          <RoleBasedRoute allowedRoles={['root_user', 'admin', 'super_manager', 'hr_admin', 'manager']}>
            <UserProductivity />
          </RoleBasedRoute>
        } />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="project-assignments" element={<ProjectAssignments />} />
                  </Route>
                </Routes>
            </Router>
          </AuthProvider>
        </ErrorBoundary>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;